"use client";

import React, { useState, useEffect, useRef } from "react";
import { createChart } from "lightweight-charts";
import {
  Box,
  Autocomplete,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import NotificationImportantIcon from "@mui/icons-material/NotificationImportant";
import LoopIcon from "@mui/icons-material/Loop";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { useFetchingData } from "../../../components/api.js";

const BOT_TOKEN = "7982681405:AAHjvdpbERCNbu9jm5-ZkfV3_oTMWM67_68";
const CHAT_ID = "-1002663905326";

export default function ChartPanel({
  symbol,
  onSymbolChange,
  symbolsList,
  baseApiUrl,
  fullscreen = false,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const api = typeof useFetchingData === "function" ? useFetchingData() : {};
  const fetcher =
    api?.__fetching_data__ ||
    api?.__fetching_data ||
    api?.fetchData ||
    api?.fetchingData ||
    api?.request ||
    null;

  const [data, setData] = useState(null);
  const [flowAcc, setFlowAcc] = useState(null);
  const [loading, setLoading] = useState(false);

  const [hToolActive, setHToolActive] = useState(false);
  const [hTarget, setHTarget] = useState("flow");
  const hTargetRef = useRef(hTarget);
  useEffect(() => {
    hTargetRef.current = hTarget;
  }, [hTarget]);

  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const priceSeriesRef = useRef(null);
  const flowSeriesRef = useRef(null);

  const hLineRef = useRef(null);
  const hLinePriceRef = useRef(null);
  const notifiedRef = useRef(false);

  const zoomedRef = useRef(false);
  const prevRange = useRef(null);
  const hToolRef = useRef(false);

  const lastGoodPayloadRef = useRef(null);
  const [usingLastAvailable, setUsingLastAvailable] = useState(false);

  function normalizeBase(input) {
    if (!input) return "/application";

    try {
      const u = new URL(input);

      if (typeof window !== "undefined" && u.host === window.location.host) {
        return u.pathname.replace(/\/+$/, "") || "/application";
      }
      return input.replace(/\/+$/, "");
    } catch {
      // ไม่ใช่ absolute URL
      return String(input).replace(/\/+$/, "") || "/application";
    }
  }

  async function callApi(url) {
    if (typeof fetcher === "function") {
      return await fetcher({ url });
    }

    const r = await fetch(url, { method: "GET" });
    const text = await r.text();

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${text.slice(0, 160)}`);
    }

    try {
      return { data: JSON.parse(text) };
    } catch {
      throw new Error(`Not JSON: ${text.slice(0, 160)}`);
    }
  }

  function extractTimeseries(res) {
    const root = res?.data ?? res;

    const maybePayload = root?.payload ?? root?.data?.payload ?? null;
    const maybeDataStr = maybePayload?.data ?? root?.payload?.data ?? null;

    if (typeof maybeDataStr === "string") {
      try {
        const parsed = JSON.parse(maybeDataStr);
        return Array.isArray(parsed) ? parsed : null;
      } catch (e) {
        console.error("❌ JSON.parse(payload.data) failed:", e);
        return null;
      }
    }

    if (Array.isArray(maybeDataStr)) return maybeDataStr;

    if (Array.isArray(root)) return root;

    return null;
  }

  // ✅ FIX TIMEZONE: ถ้า backend ส่งเวลาแบบไม่มี timezone (เช่น "2025-12-19 04:00:00")
  // ให้ถือว่าเป็น UTC แล้วเติม "Z" เพื่อกัน browser ตีความเป็นเวลาเครื่อง (ไทย) ทำให้เหลื่อม 7 ชั่วโมง
  function parseTimeToUnixSeconds(t) {
    if (!t) return null;

    const s0 = String(t).trim().replace(" ", "T");
    const hasTz = /([zZ]|[+-]\d{2}:\d{2})$/.test(s0);
    const s = hasTz ? s0 : `${s0}Z`;

    const ms = Date.parse(s);
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  }

  useEffect(() => {
    if (!symbol) {
      setData(null);
      setFlowAcc(null);
      setUsingLastAvailable(false);
      lastGoodPayloadRef.current = null;
      return;
    }

    let alive = true;

    async function fetchData() {
      try {
        setLoading(true);

        const base = normalizeBase(baseApiUrl);
        const url = `${baseApiUrl}/realtime_ws_data?symbol=${String(symbol).toUpperCase()}`;

        const res = await callApi(url);
        if (!alive) return;

        const raw = extractTimeseries(res);

        if (!raw || raw.length === 0) {
          console.warn("No timeseries from API:", res);

          if (lastGoodPayloadRef.current) {
            setUsingLastAvailable(true);
            return;
          }

          setData(null);
          setFlowAcc(null);
          setUsingLastAvailable(false);
          return;
        }

        lastGoodPayloadRef.current = res;
        setUsingLastAvailable(false);

        const sorted = [...raw].sort((a, b) => {
          const ta = parseTimeToUnixSeconds(a.time) ?? 0;
          const tb = parseTimeToUnixSeconds(b.time) ?? 0;
          return ta - tb;
        });

        const pts = [];
        const flw = [];
        let lastTs = null;

        for (const i of sorted) {
          const t = parseTimeToUnixSeconds(i.time);
          if (t == null) continue;
          if (t === lastTs) continue;
          lastTs = t;

          const lastVal = Number(i.last);
          const flowVal = Number(i.flow);

          if (Number.isFinite(lastVal)) pts.push({ time: t, value: lastVal });
          if (Number.isFinite(flowVal)) flw.push({ time: t, value: flowVal });
        }

        console.log(
          "✅ loaded",
          symbol,
          "pts =",
          pts.length,
          "flw =",
          flw.length
        );

        setData(pts.length ? pts : null);
        setFlowAcc(flw.length ? flw : null);
      } catch (err) {
        console.error("fetchData error:", err);
        if (!alive) return;

        if (lastGoodPayloadRef.current) {
          setUsingLastAvailable(true);
          return;
        }

        setData(null);
        setFlowAcc(null);
        setUsingLastAvailable(false);
      } finally {
        if (alive) setLoading(false);
      }
    }

    fetchData();
    const iv = setInterval(fetchData, 10_000);

    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [symbol, baseApiUrl]);

  useEffect(() => {
    if (!containerRef.current) return;

    const el = containerRef.current;

    const chart = createChart(el, {
      width: el.clientWidth || 300,
      height: el.clientHeight || 200,
      layout: {
        attributionLogo: false,
        // ✅ ใช้สี Deep Navy ตามที่คุณเลือก
        background: { type: 'solid', color: '#0f172a' }, 
        // ✅ ใช้สีตัวอักษรขาวนวลเพื่อให้สบายตา
        textColor: '#94a3b8', 
      },
      grid: {
        // ✅ ปรับเส้น Grid ให้เข้ากับพื้นหลังใหม่ (จางลงเพื่อความคลีน)
        horzLines: { color: 'rgba(30, 41, 59, 0.5)' },
        vertLines: { color: 'rgba(30, 41, 59, 0.5)' },
      },
      timeScale: { 
        timeVisible: true, 
        secondsVisible: false,
        borderColor: '#334155', // สีเส้นขอบแกนเวลา
      },
      rightPriceScale: { 
        visible: true, 
        borderVisible: false,
        borderColor: '#334155',
      },
      leftPriceScale: { 
        visible: true, 
        borderVisible: false, 
        position: "left",
        borderColor: '#334155',
      },
      localization: { locale: "th-TH" },
      crosshair: {
        // ปรับสีเส้นเป้าเล็ง (Crosshair)
        mode: 0,
        vertLine: { color: '#758696', labelBackgroundColor: '#758696' },
        horzLine: { color: '#758696', labelBackgroundColor: '#758696' },
      },
    });

    
    chartRef.current = chart;

    priceSeriesRef.current = chart.addLineSeries({
      priceScaleId: "right",
      color: colors.whiteChart[200],
    });

    flowSeriesRef.current = chart.addBaselineSeries({
      priceScaleId: "left",
      baseValue: { type: "price", price: 0 },
      topLineColor: colors.greenChart[100],
      bottomLineColor: colors.redChart[100],
      topFillColor1: "rgba(0,0,0,0)",
      bottomFillColor1: "rgba(0,0,0,0)",
      priceFormat: {
        type: "custom",
        minMove: 1e-8,
        formatter: (price) => {
          const absPrice = Math.abs(price);
          if (absPrice >= 1e9) return (price / 1e9).toFixed(3) + "B";
          if (absPrice >= 1e6) return (price / 1e6).toFixed(1) + "M";
          if (absPrice >= 1e5) return (price / 1e5).toFixed(1) + "K";
          if (absPrice >= 1e4) return (price / 1e4).toFixed(2) + "K";
          if (absPrice >= 1e3) return (price / 1e3).toFixed(3) + "K";
          return Number(price).toFixed(0);
        },
      },
    });

    chart.subscribeClick((param) => {
      if (!hToolRef.current || !param.point) return;

      const target =
        hTargetRef.current === "price"
          ? priceSeriesRef.current
          : flowSeriesRef.current;

      const p = target.coordinateToPrice(param.point.y);

      if (hLineRef.current) {
        hLineRef.current.applyOptions({ price: p });
      } else {
        hLineRef.current = target.createPriceLine({
          price: p,
          color: "red",
          lineWidth: 2,
          lineStyle: 2,
          axisLabelVisible: true,
          title: "H-Line",
        });
      }

      hLinePriceRef.current = p;
      notifiedRef.current = false;
    });

    let minF, maxT;
    chart.timeScale().subscribeVisibleLogicalRangeChange((rng) => {
      if (!rng) return;
      if (minF == null) [minF, maxT] = [rng.from, rng.to];
      if (prevRange.current) {
        const changed =
          Math.abs(prevRange.current.from - rng.from) > 1e-6 ||
          Math.abs(prevRange.current.to - rng.to) > 1e-6;
        if (changed) zoomedRef.current = !(rng.from <= minF && rng.to >= maxT);
      }
      prevRange.current = rng;
    });

    const ro = new ResizeObserver(() => {
      if (!el) return;
      chart.applyOptions({
        width: el.clientWidth || 300,
        height: el.clientHeight || 200,
      });
      if (!zoomedRef.current) chart.timeScale().fitContent();
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
    };
  }, [theme.palette.mode]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    if (data && priceSeriesRef.current) priceSeriesRef.current.setData(data);

    if (flowAcc && flowSeriesRef.current) {
      flowSeriesRef.current.setData(flowAcc);

      const base = flowAcc?.[0]?.value ?? 0;
      flowSeriesRef.current.applyOptions({
        baseValue: { type: "price", price: base },
      });
    }

    if (!zoomedRef.current) chart.timeScale().fitContent();
  }, [data, flowAcc]);

  useEffect(() => {
    const arr = hTarget === "price" ? data : flowAcc;
    if (!arr?.length) return;

    const lastPoint = arr[arr.length - 1];
    const last = lastPoint.value;
    const hP = hLinePriceRef.current;
    if (hP == null) return;

    if (last > hP && !notifiedRef.current) {
      notifiedRef.current = true;

      // (optional) ให้ข้อความแจ้งเตือนเป็นเวลาไทยชัวร์ ๆ
      const crossedAt = new Date(lastPoint.time * 1000).toLocaleString("th-TH", {
        timeZone: "Asia/Bangkok",
      });

      const text = [
        `${symbol} 🚀 ${hTarget.toUpperCase()} crossed H-Line!`,
        `Time:    ${crossedAt}`,
        `H-Line:  ${Number(hP).toFixed(2)}`,
        `Now:     ${Number(last).toFixed(2)}`,
      ].join("\n");

      fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chat_id: CHAT_ID, text }),
      }).catch(console.error);
    } else if (last <= hP && notifiedRef.current) {
      notifiedRef.current = false;
    }
  }, [data, flowAcc, hTarget, symbol]);

  const resetZoom = () => {
    zoomedRef.current = false;
    chartRef.current?.timeScale().fitContent();
  };

  const toggleHLine = () => {
    hToolRef.current = !hToolRef.current;
    setHToolActive(hToolRef.current);

    if (!hToolRef.current && hLineRef.current) {
      const series =
        hTarget === "price" ? priceSeriesRef.current : flowSeriesRef.current;
      series?.removePriceLine(hLineRef.current);
      hLineRef.current = null;
      hLinePriceRef.current = null;
      notifiedRef.current = false;
    }
  };

  const wrapperSx = fullscreen
    ? {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 999,
        bgcolor: "#575b69",
      }
    : {
        position: "relative",
        width: "100%",
        height: "100%",
        margin: "2px",
        bgcolor: "#575b69",
      };

  return (
    <Box sx={{ ...wrapperSx, borderRadius: 5, overflow: "hidden" }}>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "48px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
          zIndex: 10,
        }}
      >
        <Autocomplete
          size="small"
          options={symbolsList || []}
          value={symbol || null}
          onChange={(_, v) => onSymbolChange(v)}
          sx={{ width: 200 }}
          renderInput={(params) => <TextField {...params} placeholder="Symbol…" />}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControl size="small">
            <InputLabel>Notify</InputLabel>
            <Select
              value={hTarget}
              label="Notify"
              onChange={(e) => {
                setHTarget(e.target.value);
                setHToolActive(false);
                hToolRef.current = false;
              }}
            >
              <MenuItem value="flow">Flow</MenuItem>
              <MenuItem value="price">Price</MenuItem>
            </Select>
          </FormControl>

          <IconButton
            onClick={toggleHLine}
            color={hToolActive ? "secondary" : "default"}
          >
            <NotificationImportantIcon />
          </IconButton>

          {symbol && !fullscreen && (
            <IconButton
              onClick={() =>
                window.open(`/realtime_fullscreen?symbol=${symbol}`, "_blank")
              }
            >
              <ZoomInIcon />
            </IconButton>
          )}

          <IconButton onClick={resetZoom}>
            <LoopIcon />
          </IconButton>
        </Box>
      </Box>

      {loading && (!data || !flowAcc) && (
        <Box
          sx={{
            position: "absolute",
            top: "48px",
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 5,
          }}
        >
          <CircularProgress sx={{ color: "white" }} />
          <Typography sx={{ color: "white", mt: 1 }}>
            Loading… {usingLastAvailable ? "(cached)" : ""}
          </Typography>
        </Box>
      )}

      <div
        ref={containerRef}
        style={{
          position: "absolute",
          top: "48px",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      />
    </Box>
  );
}
